import {store} from "@/plugins/vuex";
import EventBus from "../../../plugins/event-bus";
import {LOCALES_LOADED} from "@/plugins/event-bus/event-bus-names";
import _ from 'lodash';
import DynamicFieldModel from "@/core/components/input-widget/DynamicFieldModel";
import BaseFieldModel from "@/core/components/input-widget/BaseFieldModel";

export default class BaseModel {
  errors = {};
  rules = {};
  attributeLabels = {};
  attributePlaceholders = {};
  attributeHints = {};
  attributeTitleHint = {};
  customMessages = {};
  sorted_dynamic_fields_template = [];

  static TRANSLATABLE_FIELDS = [];

  getTranslatableFieldsArray(type = 'text') {
    return this.constructor.TRANSLATABLE_FIELDS.map(key => {
      return new BaseFieldModel({type, key, label: this.attributeLabels[key], translatable: true})
    })
  }

  initTranslatableFields(object) {
    if (!store) {
      return;
    }

    const locales = store.state.global.locales.available_locales;
    for (let key in locales) {
      for (let field of object.constructor.TRANSLATABLE_FIELDS) {
        if (!this[field]) {
          this[field] = {};
        }
        this[field][key] = object[field][key] || '';
        this[`${field}_${key}`] = object[field][key];
        this.attributeLabels[`${field}_${key}`] = this.attributeLabels[field];
        this.attributeHints[`${field}_${key}`] = this.attributeHints[field];
        this.rules[`${field}_${key}`] = this.rules[field];
      }
    }
  }

  initDynamicFields(object, dynamicFieldsTemplate = null) {
    if (!store) {
      return;
    }
    if (!dynamicFieldsTemplate) {
      this.sorted_dynamic_fields_template = [];
      return;
    }

    const locales = store.state.global.locales.available_locales;

    const tmpTemplate = [];
    for (let key in dynamicFieldsTemplate) {
      tmpTemplate.push(new DynamicFieldModel({key, ...dynamicFieldsTemplate[key]}))
    }
    this.sorted_dynamic_fields_template = _.orderBy(tmpTemplate, ['position'], ['asc']);

    for (let locale in locales) {

      let dynamicFields = object.dynamic_fields;
      if (!dynamicFields) {
        dynamicFields = {}
        dynamicFields[locale] = {}
      }
      if (!dynamicFields[locale]) {
        dynamicFields[locale] = {}
      }

      for (let dynamicField of this.sorted_dynamic_fields_template) {
        let field = dynamicField.key;
        let options = dynamicField

        if (!this[field]) {
          this[field] = {};
        }

        const availableDynamicFieldRules = [
          'min_value',
          'max_value',
          'required',
          'max_length',
          'min_length',
          'choices',
          'allow_blank'
        ];
        let rules = {};
        if (options.rules) {
          for (let rule in options.rules) {
            if (!availableDynamicFieldRules.includes(rule)) {
              continue;
            }

            if (['min_value', 'max_value', 'required'].includes(rule)) {
              rules[rule] = options.rules[rule];
            } else if (['max_length', 'min_length'].includes(rule)) {
              rules[rule.split('_')[0]] = options.rules[rule];
            }
          }
        }

        this.rules[`${field}_${locale}`] = rules;
        this.attributeLabels[`${field}_${locale}`] = options.label;
        this.attributeHints[`${field}_${locale}`] = options.hint || "";


        if (!this[`${field}_${locale}`]) {
          if (typeof dynamicField.default_value === 'object' && dynamicField.default_value[locale]) {
            this[`${field}_${locale}`] = dynamicField.default_value[locale] ? dynamicField.default_value[locale] : null
          } else {
            this[`${field}_${locale}`] = dynamicField.default_value ? dynamicField.default_value : null
          }
          if (dynamicField.type === 'checkbox') {
            if (dynamicFields[locale][field] === true || dynamicFields[locale][field] === false) {
              this[`${field}_${locale}`] = dynamicFields[locale][field];
            } else {
              this[`${field}_${locale}`] = dynamicField.default_value ? dynamicField.default_value : false;
            }
          }
        }

        // update
        if (dynamicFields[locale][field]) {
          if (dynamicField.type === 'ckeditor') {
            this[field][locale] = `${dynamicFields[locale][field]}`
            this[`${field}_${locale}`] = `${dynamicFields[locale][field]}`
          } else {
            this[field][locale] = dynamicFields[locale][field]
            this[`${field}_${locale}`] = dynamicFields[locale][field]
          }
        } else {
          this[field][locale] = this[`${field}_${locale}`];
        }
      }
    }
  }

  assignToDynamicFields() {
    this.dynamic_fields = {}
    for (let dynamicField of this.sorted_dynamic_fields_template) {
      let field = dynamicField.key;
      for (let locale in store.state.global.locales.available_locales) {
        let valueToAssign = this[`${field}_${locale}`]

        if (dynamicField.depends_on && !this[`${dynamicField.depends_on}_${locale}`]) {
          valueToAssign = null
        }

        if (!this.dynamic_fields[locale]) {
          this.dynamic_fields[locale] = {}
        }

        this.dynamic_fields[locale][field] = valueToAssign
      }
    }
  }

  getAttributeLabel(attribute) {
    return this.attributeLabels[attribute] || attribute;
  }

  getAttributePlaceholder(attribute) {
    return this.attributePlaceholders[attribute] || attribute;
  }

  getAttributeHint(attribute) {
    return this.attributeHints[attribute] || attribute;
  }

  getAttributeTitleHint(attribute) {
    return this.attributeTitleHint[attribute] || attribute;
  }

  hasError(attribute = null) {
    if (!attribute) {
      return Object.keys(this.errors).length > 0;
    } else {
      const errors = this.errors[attribute] || [];
      return errors.length > 0;
    }
  }

  getFirstError(attribute) {
    if (!this.hasError(attribute)) {
      console.error(`Error does not exist for attribute "${attribute}"`);
      return '';
    }
    return this.errors[attribute] ? this.errors[attribute][0] : '';
  }

  addError(attribute, error) {
    this.errors[attribute] = this.errors[attribute] || [];
    this.errors[attribute].push(error);

    this.errors = {...this.errors};
  }

  setErrors(attribute, errors) {
    this.errors = {
      ...this.errors,
      [attribute]: errors
    };
  }

  setMultipleErrors(attributeErrorMap) {
    this.errors = {
      ...this.errors,
      ...attributeErrorMap
    };
  }

  resetErrors(attribute = null) {
    if (!attribute) {
      this.errors = {};
    } else {
      delete this.errors[attribute];
      this.errors = {...this.errors};
    }
  }

  toJSON() {
    const $this = {...this};
    delete $this.rules;
    delete $this.errors;
    delete $this.attributeLabels;
    delete $this.attributePlaceholders;
    delete $this.attributeHints;
    delete $this.attributeTitleHint;
    delete $this.sorted_dynamic_fields_template;
    delete $this.customMessages;
    return $this;
  }
}

let tmpArr = []

export function defineTranslatableProperties(classObject) {
  tmpArr.push(classObject)
}

EventBus.$on(LOCALES_LOADED, (locales) => {

  function defineProperties(locales, classObject) {
    for (let key in locales) {
      for (let field of classObject.TRANSLATABLE_FIELDS) {

        if (classObject.prototype.hasOwnProperty(`${field}_${key}`)) {
          continue;
        }

        Object.defineProperty(classObject.prototype, `${field}_${key}`, {
          get() {
            return this[field][key];
          },
          set(value) {
            this[field][key] = value;
          }
        });
      }
    }
  }

  for (let classObject of tmpArr) {
    defineProperties(locales.available_locales, classObject);
  }

});
